// WebSocket Service per eventi real-time
class WebSocketService {
  constructor() {
    this.ws = null;
    this.token = null;
    this.url = 'ws://api-qa.energytrades.ai:3000/ws';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
    this.isConnected = false;
    this.connectionId = null;
  }

  setToken(token) {
    this.token = token;
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = `${this.url}?token=${encodeURIComponent(this.token)}`;
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = (event) => {
          console.log('WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.emit('connected', event);
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          this.isConnected = false;
          this.emit('disconnected', event);

          if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.emit('error', error);
          reject(error);
        };

        // Timeout per connessione
        setTimeout(() => {
          if (this.ws.readyState !== WebSocket.OPEN) {
            reject(new Error('WebSocket connection timeout'));
          }
        }, 5000);

      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
    this.isConnected = false;
  }

  scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (!this.isConnected) {
        this.connect().catch(error => {
          console.error('Reconnect failed:', error);
        });
      }
    }, delay);
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('WebSocket not connected, message not sent:', message);
      return false;
    }
  }

  handleMessage(message) {
    console.log('WebSocket message received:', message);

    switch (message.type) {
      case 'welcome':
        this.connectionId = message.connectionId;
        this.emit('welcome', message);
        break;
      case 'event':
        this.emit('event', message);
        this.emit(message.eventType, message.data);
        break;
      case 'notification':
        this.emit('notification', message);
        break;
      case 'positions_update':
        this.emit('positions_update', message.data);
        break;
      case 'activations_update':
        this.emit('activations_update', message.data);
        break;
      case 'pong':
        this.emit('pong', message);
        break;
      case 'error':
        this.emit('error', message);
        break;
      default:
        this.emit('message', message);
    }
  }

  // Event listener management
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  off(eventType, callback) {
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in WebSocket event callback for ${eventType}:`, error);
        }
      });
    }
  }

  // Utility methods
  ping() {
    return this.send({ type: 'ping' });
  }

  subscribe(eventTypes) {
    return this.send({
      type: 'subscribe',
      eventTypes: Array.isArray(eventTypes) ? eventTypes : [eventTypes]
    });
  }

  unsubscribe(eventTypes) {
    return this.send({
      type: 'unsubscribe',
      eventTypes: Array.isArray(eventTypes) ? eventTypes : [eventTypes]
    });
  }

  requestPositions() {
    return this.send({ type: 'get_positions' });
  }

  requestActivations() {
    return this.send({ type: 'get_activations' });
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      connectionId: this.connectionId,
      readyState: this.ws ? this.ws.readyState : WebSocket.CLOSED
    };
  }
}

export default WebSocketService;
